import React, { useState } from "react";
import { 
  ShieldCheck, 
  AlertTriangle, 
  HeartPulse, 
  Activity, 
  Flame, 
  Zap, 
  Users, 
  Stethoscope, 
  Layers, 
  FileText,
  Award,
  CheckCircle2
} from "lucide-react";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

const CONDITION_DETAILS = {
  hta: {
    title: "Hipertensión Arterial (HTA)",
    what: "Elevación sostenida de las cifras de tensión arterial sistólica y/o diastólica en reposo.",
    calculation: "PAS ≥ 130 mmHg o PAD ≥ 80 mmHg (promedio de registros somatométricos de F1).",
    standard: "ACC/AHA 2017 High Blood Pressure Clinical Practice Guideline (American College of Cardiology / American Heart Association).",
    relevance: "Principal detonante asintomático de emergencias vasculares, cardiopatía isquémica e infartos en el entorno productivo.",
  },
  glucose: {
    title: "Hiperglicemia & Riesgo de Diabetes",
    what: "Concentración anormal de glucosa plasmática en ayunas que refleja resistencia a la insulina o diabetes manifiesta.",
    calculation: "Glucemia basal ≥ 100 mg/dL en laboratorio clínico (F2) o reporte capilar en ayunas.",
    standard: "ADA Standards of Care in Diabetes 2024 (American Diabetes Association) & Directrices de Diabetes OMS.",
    relevance: "Produce fatiga crónica, somnolencia postprandial, neuropatías y elevado ausentismo médico recurrente.",
  },
  visceral: {
    title: "Sobrepeso & Grasa Visceral (WHtR > 0.50)",
    what: "Acumulación patológica de tejido adiposo profundo intra-abdominal que rodea las vísceras vitales.",
    calculation: "Relación Cintura-Estatura (WHtR): Perímetro de cintura (cm) / Talla (cm) > 0.50.",
    standard: "International Diabetes Federation (IDF) & Ashwell et al. 2012 ('Waist-to-height ratio is a better screening tool than BMI').",
    relevance: "Predictor cardiometabólico más temprano y certero que el IMC para daño endotelial y esteatosis hepática.",
  },
  lipids: {
    title: "Dislipidemia Aterogénica",
    what: "Desbalance de lípidos y lipoproteínas aterogénicas circulantes en el torrente sanguíneo.",
    calculation: "Colesterol total ≥ 200 mg/dL o Triglicéridos ≥ 150 mg/dL en perfil lipídico en ayunas (F2).",
    standard: "National Cholesterol Education Program (NCEP) Adult Treatment Panel III (ATP III Guidelines).",
    relevance: "Acelera la arteriosclerosis silente en arterias coronarias y carotídeas, elevando el riesgo cardiovascular colectivo.",
  },
  emotional: {
    title: "Sobrecarga Emocional & Tensión Autonómica",
    what: "Respuesta de alarma neurovegetativa sostenida y agotamiento de la reserva psíquica o emocional.",
    calculation: "Índice de Tensión de Baevsky > 400 pts (rPPG FaceScan) o nivel severo en Maslach Burnout Inventory (F5).",
    standard: "Baevsky RM Stress Index (VFC / rPPG) & Maslach Burnout Inventory (MBI) validado por OMS CIE-11 (QD85).",
    relevance: "Dispara el presentismo, incrementa la tasa de errores operacionales y acelera la rotación laboral.",
  },
  sedentary: {
    title: "Inactividad Física & Sedentarismo",
    what: "Patrón de bajo gasto calórico semanal asociado a tiempos prolongados en postura sentada durante la jornada.",
    calculation: "Gasto < 600 MET-minutos/semana en Cuestionario IPAQ o > 8 horas continuas al día en postura sedente.",
    standard: "Organización Mundial de la Salud (OMS 2020 Guidelines on Physical Activity) & IPAQ Research Committee.",
    relevance: "Factor de riesgo independiente de trombosis venosa profunda, lumbalgias incapacitantes y rigidez metabólica.",
  },
};

const CONCURRENCE_DETAILS = {
  c0: {
    title: "0 Factores: Aparentemente Sanos",
    what: "Colaboradores con todos sus biomarcadores clínicos y hábitos de vida dentro de rangos normales de protección.",
    calculation: "Ausencia de HTA (<130/80), normoglucemia (<100), WHtR ≤ 0.50, lípidos normales, estrés regulado y actividad física regular.",
    standard: "American Heart Association 'Life's Essential 8' & Definición de Resiliencia Biológica OMS.",
    relevance: "Foco en blindaje y refuerzo positivo para sostener la productividad y cultura de salud corporativa.",
  },
  c1: {
    title: "1 Factor: Monitoreo Preventivo",
    what: "Presencia de un único factor de riesgo aislado (ej. sedentarismo o sobrepeso incipiente) sin daño a órgano blanco.",
    calculation: "Cumple exactamente 1 de los 6 criterios de corte clínico internacional.",
    standard: "Guías de Prevención Primaria de la OIT/OMS & Protocolos de Intervención Temprana de Salud Ocupacional.",
    relevance: "Ventana de máxima costo-efectividad: revertir un factor aislado evita el desarrollo de enfermedades crónicas.",
  },
  c2: {
    title: "2 Factores: Riesgo Moderado",
    what: "Combinación de dos condiciones desfavorables que inician una sinergia deletérea (ej. grasa visceral + prehipertensión).",
    calculation: "Cumple exactamente 2 criterios simultáneos de las 6 condiciones evaluadas.",
    standard: "Consenso ESC/ACC de Prevención Primaria de Riesgo Cardiovascular Moderado.",
    relevance: "Requiere planes dirigidos de nutrición y actividad física supervisados por el Oficial de Salud (IHO).",
  },
  c3: {
    title: "3 Factores: Clúster Metabólico",
    what: "Tríada clínica patológica compatible con el Síndrome Metabólico de alta morbimortalidad.",
    calculation: "Cumple 3 factores simultáneos (ej. WHtR > 0.50 + PAS ≥ 130 + Glucosa ≥ 100 mg/dL).",
    standard: "Definición Unificada de Síndrome Metabólico: Consensus NCEP ATP III / AHA / NHLBI / IDF 2009.",
    relevance: "Multiplica por 3 el riesgo de eventos vasculares agudos y quintuplica los días de incapacidad laboral.",
  },
  c4Plus: {
    title: "4+ Factores: Patología Activa",
    what: "Acumulación severa de multimorbilidad cardiometabólica y psicosocial con daño orgánico inminente o establecido.",
    calculation: "Co-ocurrencia de 4 o más de los 6 factores de corte clínico evaluados en el protocolo.",
    standard: "Guías Clínicas de Multimorbilidad Crónica Compleja (NICE NG56 / OMS Chronic Care Model).",
    relevance: "Requiere canalización médica directa prioritaria a especialista y control estricto por Medicina del Trabajo.",
  },
};

export default function PrevalenceTableCard({ metrics, totalEmployees = 0 }) {
  const [activeView, setActiveView] = useState("prevalence"); // 'prevalence' | 'concurrence'

  const prevalenceTable = metrics?.prevalenceTable || [];
  const concurrence = metrics?.concurrence || {};
  const sampleSize = metrics?.sampleSize || totalEmployees || 1;

  const concurrenceKeys = ["c0", "c1", "c2", "c3", "c4Plus"];

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm relative mb-6"
      data-testid="prevalence-table-card"
    >
      {/* Cabecera */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
              <Award size={12} />
              Consensos Científicos Internacionales
            </span>
            <span className="text-xs text-slate-400">
              Muestra evaluada: <strong>{sampleSize}</strong> colaboradores
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-lg font-bold text-slate-800">
              Prevalencia de Patologías & Concurrencia de Factores
            </h3>
            <WidgetInfoTooltip
              title="Prevalencia & Concurrencia de Factores Clínicos"
              align="left"
              what="Tabulador epidemiológico de las 6 condiciones clínicas de alta prevalencia y estratificación poblacional según el número simultáneo de factores de riesgo que presenta cada colaborador (de 0 a 4+)."
              calculation="Cálculo individualizado cruzando somatometría (WHtR/IMC), tensión arterial sisto-diastólica (ACC/AHA 2017), glucemia basal (ADA 2024), perfil lipídico (NCEP ATP III), esfera emocional (Baevsky/Maslach) e inactividad física (IPAQ). Clasificados en 5 cohortes de concurrencia: 0 (Aparentemente sanos), 1, 2, 3 y 4+ o patología activa."
              standard="ACC/AHA 2017 (Tensión) • ADA 2024 (Glucemia) • IDF/Ashwell 2012 (Grasa Visceral) • NCEP ATP III (Lípidos) • Baevsky RM / Maslach MBI (Estrés) • OMS IPAQ 2020 (Actividad Física)."
              relevance="Permite al Directorio y a Recursos Humanos dimensionar el riesgo real de la empresa sin sesgos: cuántas personas están verdaderamente sanas, cuántas en riesgo incipiente y cuántas en alarma prioritaria para planificar los programas de bienestar y cobertura médica."
            />
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Estratificación clínica validada según consensos ACC/AHA, ADA, IDF, NCEP ATP III, OMS y Baevsky.
          </p>
        </div>

        {/* Selector de Vista (Tabs) */}
        <div className="flex items-center bg-slate-100/90 p-1 rounded-xl border border-slate-200/60 self-start lg:self-auto">
          <button
            type="button"
            data-testid="toggle-prevalence"
            onClick={() => setActiveView("prevalence")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeView === "prevalence"
                ? "bg-white text-slate-800 shadow-xs border border-slate-200/50"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <FileText size={13} />
            <span>Prevalencia de las 6 Condiciones</span>
          </button>
          <button
            type="button"
            data-testid="toggle-concurrence"
            onClick={() => setActiveView("concurrence")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeView === "concurrence"
                ? "bg-white text-slate-800 shadow-xs border border-slate-200/50"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Layers size={13} />
            <span>Concurrencia de Factores (0 a 4+)</span>
          </button>
        </div>
      </div>

      {/* VISTA 1: TABLA DE LAS 6 CONDICIONES CLÍNICAS */}
      {activeView === "prevalence" && (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-500 font-semibold">
                  <th className="py-3 px-4">Condición Clínica Evaluada</th>
                  <th className="py-3 px-4 text-center">Afectados</th>
                  <th className="py-3 px-4">Consenso Científico & Criterio Internacional</th>
                  <th className="py-3 px-4 text-center">Estado SST</th>
                  <th className="py-3 px-4">Acción Recomendada</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {prevalenceTable.map((row) => {
                  const details = CONDITION_DETAILS[row.id];
                  return (
                    <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="py-3.5 px-4 font-semibold text-slate-800">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: row.color }}
                          />
                          <span>{row.condition}</span>
                          {details && (
                            <WidgetInfoTooltip
                              title={details.title}
                              size="sm"
                              align="left"
                              what={details.what}
                              calculation={details.calculation}
                              standard={details.standard}
                              relevance={details.relevance}
                            />
                          )}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="inline-flex items-baseline gap-1">
                          <span className="text-base font-bold text-slate-800">
                            {row.status === "Sin datos" ? "—" : `${row.affectedPercent}%`}
                          </span>
                          <span className="text-[11px] text-slate-400">({row.affectedCount} colab.)</span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-slate-600 font-medium">
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200/80">
                          {row.consensus}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${
                            row.status === "Sin datos"
                              ? "bg-slate-50 text-slate-500 border-slate-200"
                              : row.status === "Alerta"
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : row.status === "Moderado"
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-slate-500 text-[11px]">
                        {row.id === "hta" && "Jornada de control de tensión y sugerencia de consulta con Médico General."}
                        {row.id === "glucose" && "Reevaluación en ayunas y módulo educativo de nutrición de bajo índice glicémico."}
                        {row.id === "visceral" && "Programa de reducción de perímetro abdominal y compensación nutricional."}
                        {row.id === "lipids" && "Sugerencia de perfil lipídico sérico y hábitos cardio-protectores."}
                        {row.id === "emotional" && "Cápsulas de autorregulación autonómica, higiene del sueño y apoyo psicosocial."}
                        {row.id === "sedentary" && "Pausas activas estructuradas de 10 min por turno laboral y retos de movilidad."}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-3.5 bg-slate-50 rounded-xl border border-slate-200/60 flex items-start gap-3">
            <CheckCircle2 size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-600 leading-relaxed">
              <strong>Evidencia Científica Integrada:</strong> Los umbrales de detección no son arbitrarios; aplican las directrices clínicas de la <em>American Heart Association (ACC/AHA)</em>, <em>American Diabetes Association (ADA)</em>, <em>International Diabetes Federation (IDF)</em> y la <em>Organización Mundial de la Salud (OMS)</em> para garantizar que el plan de salud corporativo tenga respaldo epidemiológico oficial.
            </p>
          </div>
        </div>
      )}

      {/* VISTA 2: CONCURRENCIA DE FACTORES (0 A 4+) */}
      {activeView === "concurrence" && (
        <div>
          {/* Barra apilada interactiva de concurrencia */}
          <div className="mb-6">
            <div className="flex justify-between items-center text-xs text-slate-600 font-medium mb-2">
              <span>Distribución por Acumulación de Factores de Riesgo</span>
              <span className="text-slate-400">Total: {sampleSize} colaboradores</span>
            </div>
            <div className="h-4 rounded-full overflow-hidden flex bg-slate-100 border border-slate-200/80 shadow-inner">
              {concurrenceKeys.map((key) => {
                const item = concurrence[key];
                if (!item || item.percent === 0) return null;
                return (
                  <div
                    key={key}
                    style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                    className="h-full transition-all duration-500 relative group cursor-pointer"
                    title={`${item.label}: ${item.percent}% (${item.count} colab.)`}
                  />
                );
              })}
            </div>
          </div>

          {/* Rejilla de 5 niveles de concurrencia */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
            {concurrenceKeys.map((key) => {
              const item = concurrence[key] || {
                count: 0,
                percent: 0,
                label: key,
                sublabel: "",
                color: "#94A3B8",
                badge: "Sin datos",
                action: "",
              };
              const details = CONCURRENCE_DETAILS[key];
              return (
                <div
                  key={key}
                  className="rounded-xl border border-slate-200/80 p-4 bg-slate-50/50 hover:bg-slate-50 transition-all flex flex-col justify-between"
                  style={{ borderTop: `4px solid ${item.color}` }}
                >
                  <div>
                    <div className="flex items-center justify-between gap-1 mb-2">
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: `${item.color}15`,
                          color: item.color,
                        }}
                      >
                        {item.badge}
                      </span>
                      {details && (
                        <WidgetInfoTooltip
                          title={details.title}
                          size="sm"
                          align="center"
                          what={details.what}
                          calculation={details.calculation}
                          standard={details.standard}
                          relevance={details.relevance}
                        />
                      )}
                    </div>
                    <div className="text-xl font-bold text-slate-800">{item.percent}%</div>
                    <div className="text-xs font-semibold text-slate-700 mt-0.5">{item.label}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{item.sublabel}</div>
                    <div className="text-[11px] font-bold text-slate-500 mt-1">
                      {item.count} colaboradores
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-200/60 text-[11px] text-slate-500 leading-snug">
                    {item.action}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-3.5 bg-amber-50/80 rounded-xl border border-amber-200/70 flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-relaxed">
              <strong>Impacto en Salud Laboral:</strong> Los colaboradores con <strong>3 o más factores concurrentes (clúster metabólico)</strong> representan la mayor probabilidad de ausentismo médico recurrente y eventos cardiovasculares graves. La estrategia recomendada consiste en estabilizar el riesgo temprano en las cohortes de 1 y 2 factores para frenar la transición hacia el clúster de alta severidad.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
